<template>
  <div class="ui-page">
    <AppFilterToolbar>
      <template #primary><app-input v-model="search" design="new" type="search" aria-label="Search tickets" placeholder="Search by client, file name or flight number" /></template>
      <template #selection><app-filters v-model="queue" design="new" refined weight="medium" name="flight-ticket-queue" :filters="queueOptions" aria-label="Ticket queue" /></template>
      <template v-if="search" #clear><AppClearFiltersButton design="new" icon-only @clear="search = null" /></template>
    </AppFilterToolbar>
    <AppContentSkeleton v-if="loading" label="Loading tickets" :columns="columns" min-width="1200px" />
    <AppErrorState v-else-if="error" :message="error" icon-only @retry="fetchItems" />
    <app-no-results v-else-if="!items.length" design="new" :title="search ? 'No matching tickets' : 'No tickets'" text="" />
    <template v-else-if="queue === 'in_review'">
      <ticket-table v-for="section in reviewSections" :key="section.code" :title="section.title" :items="section.items" @action="onAction" />
    </template>
    <ticket-table v-else :items="items" :show-statuses="queue !== 'new'" @action="onAction" />
    <AppPagination v-if="!loading && !error" design="new" placement="embedded" :page="pagination.page" :pages="pagination.pages" :count="pagination.count" :visible-count="items.length" :page-size="pagination.pageSize" @change="onPageChange" />
    <review-modal ref="reviewModal" @changed="fetchItems" @recognized="onRecognized" />
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import { getSupportFlightTicketReconciliation } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import ReviewModal from './ReviewModal'
import TicketTable from './TicketTable'

const REVIEW_SECTIONS = [
  { code: 'ready', title: 'Ready to apply', statuses: ['ready'] },
  { code: 'review_required', title: 'Needs review', statuses: ['review_required'] },
  { code: 'failed', title: 'Recognition failed', statuses: ['failed'] },
  { code: 'processing', title: 'AI is working', statuses: ['processing'] },
  { code: 'needs_analysis', title: 'Needs AI analysis', statuses: ['not_analyzed', 'new'] },
]

export default {
  name: 'TechSupportFlightTicketsRelevant',
  components: { AppContentSkeleton, AppErrorState, ReviewModal, TicketTable },
  data() {
    return {
      items: [],
      error: '',
      queue: 'new',
      queueOptions: [
        { value: 'new', label: 'New' },
        { value: 'in_review', label: 'In review' },
        { value: 'completed', label: 'Completed' },
      ],
      search: null,
      loading: false,
      pagination: {
        page: 1,
        pages: 1,
        count: 0,
        pageSize: 20,
      },
    }
  },
  computed: {
    columns() { return TicketTable.columns },
    reviewSections() {
      return REVIEW_SECTIONS.map(section => ({
        ...section,
        items: this.items.filter(item => (
          item.reconciliation_state && section.statuses.includes(item.reconciliation_state.code)
        )),
      })).filter(section => section.items.length)
    },
  },
  watch: {
    queue() {
      this.pagination.page = 1
      this.fetchItems()
    },
  },
  created() {
    this.onSearch = debounce(() => {
      this.pagination.page = 1
      this.fetchItems()
    }, 350)
    this.$watch('search', this.onSearch)
    this.fetchItems()
  },
  beforeDestroy() {
    this.onSearch.cancel()
  },
  methods: {
    fetchItems() {
      this.error = ''
      this.loading = true
      return getSupportFlightTicketReconciliation({
        queue: this.queue,
        search: this.search || undefined,
        page: this.pagination.page,
        page_size: this.pagination.pageSize,
      }).then(response => {
        this.items = response.data.results
        this.pagination.count = response.data.count
        this.pagination.pages = response.data.pages
      }).catch(() => {
        this.error = 'Tickets could not be loaded. Please try again.'
      }).finally(() => {
        this.loading = false
      })
    },
    onPageChange(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onAction(item) {
      this.$refs.reviewModal.open(item.id)
    },
    onRecognized() {
      if (this.queue === 'in_review') {
        this.fetchItems()
      } else {
        this.queue = 'in_review'
      }
    },
  },
}
</script>
